/**
 * Pure state for the reconnect cadence editor so it can be unit tested without React.
 */
import type { CadenceName, CadenceQualifier, ReconnectCadenceState, SaveCadenceInput } from "@hub/shared";

export type CadenceMode = "auto" | "disabled" | CadenceName;

export interface CadenceDraft {
  mode: CadenceMode;
  qualifier: CadenceQualifier | null;
  weekday: number | null; // 0..6
  monthDay: number | null; // 1..31
  month: number | null; // 1..12
}

export const CADENCE_OPTIONS: { id: CadenceMode; label: string; hint: string }[] = [
  { id: "auto", label: "Automatic", hint: "Hub picks a cadence from how often you usually talk" },
  { id: "weekly", label: "Weekly", hint: "Every week, optionally on a chosen day" },
  { id: "monthly", label: "Monthly", hint: "Every month, optionally on a specific day or weekday" },
  { id: "quarterly", label: "Quarterly", hint: "Every three months" },
  { id: "yearly", label: "Yearly", hint: "Once a year, optionally in a chosen month" },
  { id: "disabled", label: "Off", hint: "Never suggest reconnecting with this person" },
];

export const QUALIFIERS: { id: CadenceQualifier; label: string }[] = [
  { id: "first", label: "First" },
  { id: "second", label: "Second" },
  { id: "third", label: "Third" },
  { id: "fourth", label: "Fourth" },
  { id: "last", label: "Last" },
];

export const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function draftFromState(state: ReconnectCadenceState): CadenceDraft {
  if (state.state === "auto") return { mode: "auto", qualifier: null, weekday: null, monthDay: null, month: null };
  if (state.state === "disabled") return { mode: "disabled", qualifier: null, weekday: null, monthDay: null, month: null };
  return { mode: state.state, qualifier: state.qualifier ?? null, weekday: state.row.weekdayNumber, monthDay: state.row.dayNumber, month: state.row.monthNumber };
}

export function setMode(draft: CadenceDraft, mode: CadenceMode): CadenceDraft {
  // Changing the cadence clears qualifiers that no longer apply.
  const keepsWeekday = mode === "weekly" || mode === "monthly";
  return { mode, qualifier: mode === "monthly" ? draft.qualifier : null, weekday: keepsWeekday ? draft.weekday : null, monthDay: mode === "monthly" || mode === "yearly" ? draft.monthDay : null, month: mode === "yearly" ? draft.month : null };
}

export function toSaveInput(contactId: string, draft: CadenceDraft): SaveCadenceInput {
  if (draft.mode === "auto") return { contactId, cadence: null, isAutomatic: true, isDisabled: false };
  if (draft.mode === "disabled") return { contactId, cadence: null, isDisabled: true, isAutomatic: false };
  return {
    contactId,
    cadence: draft.mode,
    qualifier: draft.mode === "monthly" && draft.weekday !== null ? draft.qualifier : null,
    weekdayNumber: draft.mode === "weekly" || draft.mode === "monthly" ? draft.weekday : null,
    dayNumber: (draft.mode === "monthly" && draft.weekday === null) || draft.mode === "yearly" ? draft.monthDay : null,
    monthNumber: draft.mode === "yearly" ? draft.month : null,
    isDisabled: false,
    isAutomatic: false,
  };
}

export function describeDraft(draft: CadenceDraft): string {
  switch (draft.mode) {
    case "auto": return "Hub decides based on your relationship strength.";
    case "disabled": return "No reconnect suggestions for this person.";
    case "weekly": return draft.weekday === null ? "Every week." : `Every ${WEEKDAYS[draft.weekday]}.`;
    case "monthly":
      if (draft.weekday !== null) return `Every month on the ${(draft.qualifier ?? "first")} ${WEEKDAYS[draft.weekday]}.`;
      if (draft.monthDay !== null) return `Every month on day ${draft.monthDay}.`;
      return "Every month.";
    case "quarterly": return "Every quarter.";
    case "yearly":
      if (draft.month !== null && draft.monthDay !== null) return `Every year on ${MONTHS[draft.month - 1]} ${draft.monthDay}.`;
      if (draft.month !== null) return `Every year in ${MONTHS[draft.month - 1]}.`;
      return "Every year.";
  }
}
